import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { searchGenes } from '../services/api';

const HighlightText = ({ text, highlight }) => {
  if (!highlight || !text) return text || '';
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ bgcolor: 'warning.light', borderRadius: 0.5, px: 0.25 }}>
        {text.slice(idx, idx + highlight.length)}
      </Box>
      {text.slice(idx + highlight.length)}
    </>
  );
};

const SearchBar = ({ onSelect }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (inputValue.length < 1) {
      setOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchGenes(inputValue, 10);
        setOptions(data.results || []);
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleChange = (event, newValue) => {
    if (newValue) {
      if (onSelect) {
        onSelect(newValue);
      } else {
        navigate(`/gene/${newValue.symbol}`);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (options.length === 1) {
        navigate(`/gene/${options[0].symbol}`);
      } else if (inputValue.trim()) {
        navigate(`/gene/${inputValue.trim()}`);
      }
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : `${option.symbol} - ${option.name || option.ensembl_id}`
      }
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={handleChange}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search genes by symbol or Ensembl ID"
          variant="outlined"
          fullWidth
          onKeyDown={handleKeyDown}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Box sx={{ fontWeight: 'bold' }}>
            <HighlightText text={option.symbol} highlight={inputValue} />
          </Box>
          {option.name && (
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              <HighlightText text={option.name} highlight={inputValue} />
            </Box>
          )}
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {option.ensembl_id}
          </Box>
        </Box>
      )}
      PaperComponent={({ children, ...other }) => (
        <Paper {...other} sx={{ mt: 1 }}>
          {children}
        </Paper>
      )}
    />
  );
};

export default SearchBar;
